import { useCallback, useEffect, useState } from 'react';
import { urqlGql } from '@pypestream/api-services';

import {
  normalizeLanguagesCatalog,
  type LanguageCatalogEntry,
} from '../language-catalog';

/*
 * Both endpoints are static catalogs (Engagement serves them from code, not a
 * database), so one successful answer per page load is enough and every form
 * and entry shares it. A failure is not cached: the next mount, or Retry,
 * asks again.
 */
let languagesRequest: Promise<LanguageCatalogEntry[]> | null = null;
/** The resolved catalog, so a later mount starts loaded instead of flashing. */
let resolvedLanguages: LanguageCatalogEntry[] | null = null;
const accentsRequests = new Map<string, Promise<string[]>>();

const fetchLanguages = (): Promise<LanguageCatalogEntry[]> => {
  if (languagesRequest) return languagesRequest;
  const request = urqlGql
    .getVoiceAiLanguages()
    .toPromise()
    .then((response) => {
      if (response.error) throw response.error;
      resolvedLanguages = normalizeLanguagesCatalog(
        response.data?.getVoiceAiLanguages?.languages
      );
      return resolvedLanguages;
    })
    .catch((error: unknown) => {
      languagesRequest = null;
      throw error;
    });
  languagesRequest = request;
  return request;
};

const fetchAccents = (language: string): Promise<string[]> => {
  const cached = accentsRequests.get(language);
  if (cached) return cached;
  const request = urqlGql
    .getVoiceAiAccents({ args: { language } })
    .toPromise()
    .then((response) => {
      if (response.error) throw response.error;
      return (response.data?.getVoiceAiAccents?.accents || []).filter(
        (a): a is string => typeof a === 'string' && !!a
      );
    })
    .catch((error: unknown) => {
      accentsRequests.delete(language);
      throw error;
    });
  accentsRequests.set(language, request);
  return request;
};

export type CatalogState<T> = {
  data: T;
  isLoading: boolean;
  error: unknown;
  retry: () => void;
};

/** The supported languages and locales (`GET /voice_ai/v2/languages`). */
export const useVoiceAiLanguages = (): CatalogState<LanguageCatalogEntry[]> => {
  const [data, setData] = useState<LanguageCatalogEntry[]>(
    () => resolvedLanguages ?? []
  );
  const [isLoading, setIsLoading] = useState(() => !resolvedLanguages);
  const [error, setError] = useState<unknown>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    if (!resolvedLanguages) setIsLoading(true);
    setError(null);
    fetchLanguages()
      .then((languages) => active && setData(languages))
      .catch((e) => {
        console.error('[LanguagesBlock] getVoiceAiLanguages failed', e);
        if (active) setError(e);
      })
      .finally(() => active && setIsLoading(false));
    return () => {
      active = false;
    };
  }, [attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return { data, isLoading, error, retry };
};

/** The voice accents of one language (`GET /voice_ai/v2/accents`). */
export const useVoiceAiAccents = (language: string): CatalogState<string[]> => {
  const [data, setData] = useState<string[]>([]);
  // Loading from the first render when there is a language to load for, so
  // the accent select never shows "no accents" before the request starts.
  const [isLoading, setIsLoading] = useState(() => !!language);
  const [error, setError] = useState<unknown>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!language) {
      setData([]);
      return undefined;
    }
    let active = true;
    setIsLoading(true);
    setError(null);
    fetchAccents(language)
      .then((accents) => active && setData(accents))
      .catch((e) => {
        console.error('[LanguagesBlock] getVoiceAiAccents failed', e);
        if (active) {
          setData([]);
          setError(e);
        }
      })
      .finally(() => active && setIsLoading(false));
    return () => {
      active = false;
    };
  }, [language, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return { data, isLoading, error, retry };
};
